import { Fragment, useEffect } from "react";
import { valorPorExtenso } from "../../../shared/extenso";
import { COMPOSICOES_CANTEIRO, secoesMemoria, tipoDoCodigo, type ParametrosCanteiro } from "../../../shared/canteiro";
import { NOTA_MATERIAL_SERVICO } from "../../../shared/tipos";
import type { OrcamentoItemCalculado, SecaoCalculada } from "../../../shared/tipos";
import type { ComposicaoCalculada } from "../../../shared/tipos";
import { useOrcamento } from "../api/consultas";
import { BLOCOS_COMPOSICAO } from "../components/orcamento";
import { Carregando, ErroCarregamento, formatarData, formatarMoeda, formatarQuantidade } from "../components/ui";
import { useIdOrcamento } from "./OrcamentoDetalhe";

/** Versão para impressão/PDF (≙ orcamento_imprimir.html): fundo claro e diálogo de impressão automático. */
export function OrcamentoImprimir() {
  const id = useIdOrcamento();
  const { data, isLoading, error } = useOrcamento(id);

  useEffect(() => {
    document.body.classList.add("modo-impressao");
    return () => document.body.classList.remove("modo-impressao");
  }, []);

  useEffect(() => {
    if (!data) return;
    document.title = `Orçamento - ${data.orcamento.nome}`;
    const t = setTimeout(() => window.print(), 300);
    return () => clearTimeout(t);
  }, [data]);

  if (isLoading) return <Carregando />;
  if (error || !data) return <ErroCarregamento erro={error} />;
  const { orcamento, resumo, canteiro } = data;
  const semSecao = resumo.itens.filter((it) => it.secao_id == null);
  const gruposItens: { chave: string; secao: SecaoCalculada | null; itens: OrcamentoItemCalculado[] }[] =
    resumo.secoes.length === 0
      ? [{ chave: "todos", secao: null, itens: resumo.itens }]
      : [
          ...resumo.secoes.map((sec) => ({ chave: `s${sec.id}`, secao: sec, itens: resumo.itens.filter((it) => it.secao_id === sec.id) })),
          ...(semSecao.length ? [{ chave: "sem", secao: null, itens: semSecao }] : []),
        ];

  return (
    <div className="pagina-impressao">
      <style>{"@page { size: A4 landscape; margin: 10mm; }"}</style>
      <h1>Orçamento: {orcamento.nome}</h1>
      <p className="meta">
        {resumo.municipio && <>Município: {resumo.municipio.nome} · </>}
        Regime: {orcamento.regime} · Base de preços EMBASA Junho/2026-R2 · Emitido em {formatarData(orcamento.criado_em)}
      </p>

      <h2>1. Orçamento</h2>
      <table className="tabela-itens">
        <thead>
          <tr>
            <th>Item</th>
            <th>Código</th>
            <th>Descrição</th>
            <th>Un.</th>
            <th className="num">Qtd.</th>
            <th className="num">Preço Unit.</th>
            <th className="num">Total</th>
          </tr>
        </thead>
        <tbody>
          {gruposItens.map((g) => (
            <Fragment key={g.chave}>
              {g.secao && (
                <tr className="linha-secao">
                  <td colSpan={6}>
                    <strong>{g.secao.numero}</strong> {g.secao.titulo}
                  </td>
                  <td className="num">{g.secao.total_itens > 0 ? formatarMoeda(g.secao.total_itens) : ""}</td>
                </tr>
              )}
              {g.itens.map((it, i) => (
                <tr key={it.id}>
                  <td>{g.secao ? `${g.secao.numero}.${i + 1}` : String(i + 1)}</td>
                  <td>{it.codigo_snapshot}</td>
                  <td>{it.descricao_snapshot}</td>
                  <td>{it.unidade_snapshot}</td>
                  <td className="num">{formatarQuantidade(it.quantidade)}</td>
                  <td className="num">{formatarMoeda(it.preco_unitario)}</td>
                  <td className="num">{formatarMoeda(it.total)}</td>
                </tr>
              ))}
            </Fragment>
          ))}
          {resumo.itens.length > 0 && (
            <tr className="linha-subtotal">
              <td colSpan={6} style={{ textAlign: "right" }}>
                Subtotal
              </td>
              <td className="num">{formatarMoeda(resumo.subtotal_materiais + resumo.subtotal_servicos)}</td>
            </tr>
          )}
        </tbody>
      </table>

      {BLOCOS_COMPOSICAO.filter(({ tipo }) => resumo.blocos_especiais[tipo].length > 0).map(({ tipo, rotulo }) => (
        <section key={tipo}>
          <h2>{rotulo}</h2>
          <table className="tabela-composicao">
            <thead>
              <tr className="linha-composicao">
                <td>{COMPOSICOES_CANTEIRO[tipo].codigo.replace("/EMBASA", "")}</td>
                <td colSpan={4}>{COMPOSICOES_CANTEIRO[tipo].descricao}</td>
                <td>UN</td>
                <td className="num">{formatarMoeda(resumo.totais_especiais[tipo])}</td>
              </tr>
              <tr>
                <th>Código</th>
                <th>Descrição do insumo ou serviço</th>
                <th>Tipo</th>
                <th>Un</th>
                <th className="num">Coef.</th>
                <th className="num">Cotação</th>
                <th className="num">Total</th>
              </tr>
            </thead>
            <tbody>
              {resumo.blocos_especiais[tipo].map((c) => (
                <tr key={c.id}>
                  <td>{c.codigo_referencia || "—"}</td>
                  <td>{c.descricao}</td>
                  <td>{c.codigo_referencia ? tipoDoCodigo(c.codigo_referencia) : "—"}</td>
                  <td>{c.unidade}</td>
                  <td className="num">{formatarQuantidade(c.quantidade)}</td>
                  <td className="num">{formatarMoeda(c.valor_unitario)}</td>
                  <td className="num">{formatarMoeda(c.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}

      {resumo.itens_cotacao.length > 0 && (
        <section>
          <h2>Itens para cotação (sem preço na tabela EMBASA)</h2>
          <table>
            <thead>
              <tr>
                <th>Código (planilha)</th>
                <th>Descrição</th>
                <th>Un.</th>
                <th className="num">Qtd.</th>
              </tr>
            </thead>
            <tbody>
              {resumo.itens_cotacao.map((ic) => (
                <tr key={ic.id}>
                  <td>{ic.codigo_planilha || "—"}</td>
                  <td>{ic.descricao}</td>
                  <td>{ic.unidade || "—"}</td>
                  <td className="num">{formatarQuantidade(ic.quantidade)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <h2>5. Resumo Financeiro</h2>
      <div className="total-line">
        <span>Subtotal serviços (itens + mobilização/desmob./adm. local)</span>
        <span>R$ {formatarMoeda(resumo.subtotal_servicos_com_especiais)}</span>
      </div>
      <div className="total-line">
        <span>Subtotal materiais</span>
        <span>R$ {formatarMoeda(resumo.subtotal_materiais)}</span>
      </div>
      {resumo.bdi_servicos_pct !== null && (
        <div className="total-line">
          <span>BDI Serviços ({resumo.bdi_servicos_pct}%)</span>
          <span>R$ {formatarMoeda(resumo.valor_bdi_servicos)}</span>
        </div>
      )}
      {resumo.bdi_materiais_pct !== null && (
        <div className="total-line">
          <span>BDI Materiais ({resumo.bdi_materiais_pct}%)</span>
          <span>R$ {formatarMoeda(resumo.valor_bdi_materiais)}</span>
        </div>
      )}
      <div className="total-line grand">
        <span>TOTAL GERAL</span>
        <span>R$ {formatarMoeda(resumo.total_geral)}</span>
      </div>
      <p style={{ textAlign: "right", fontStyle: "italic", color: "#555", fontSize: 15 }}>({valorPorExtenso(resumo.total_geral)})</p>

      {canteiro && <MemoriaCanteiro parametros={canteiro} composicaoAdm={resumo.blocos_especiais.administracao_local} />}

      <p className="note">
        <strong>Este orçamento foi gerado com base nas tabelas oficiais de preços da EMBASA</strong> (data-base
        Junho/2026-R2), incluindo códigos, preços unitários e encargos sociais por sindicato regional. {NOTA_MATERIAL_SERVICO}{" "}
        BDI de referência 2023.05 — confirmar atualização com a EMBASA.
      </p>
      <p className="note" style={{ textAlign: "center", marginTop: 24, borderTop: "1px solid #ccc", paddingTop: 10 }}>
        Programa desenvolvido por Wladmir Vieira · © 2026
      </p>
    </div>
  );
}

/** Anexo: memória de cálculo do canteiro (mesma sequência da planilha de referência). */
function MemoriaCanteiro({ parametros, composicaoAdm }: { parametros: ParametrosCanteiro; composicaoAdm: ComposicaoCalculada[] }) {
  const adm = parametros.administracao;
  const descricaoDe = (codigo: string) => composicaoAdm.find((c) => c.codigo_referencia === codigo)?.descricao ?? codigo;
  return (
    <section className="memoria-canteiro">
      <h2>Anexo — Memória de cálculo: mobilização, desmobilização e administração local</h2>
      <div className="memoria-grade">
        {secoesMemoria(parametros).map((secao) => (
          <table key={secao.titulo}>
            <thead>
              <tr>
                <th colSpan={2}>{secao.titulo}</th>
              </tr>
            </thead>
            <tbody>
              {secao.linhas.map((l) => (
                <tr key={l.rotulo}>
                  <td>{l.rotulo}</td>
                  <td className="num" style={{ whiteSpace: "nowrap" }}>
                    {formatarQuantidade(Math.round(l.valor * 1000) / 1000)} {l.unidade}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ))}
      </div>
      <table>
        <thead>
          <tr>
            <th colSpan={5}>8 - Administração local (coeficiente = quantidade × multiplicador)</th>
          </tr>
          <tr>
            <th>Código</th>
            <th>Insumo / recurso</th>
            <th className="num">Quantidade</th>
            <th className="num">Multiplicador</th>
            <th className="num">Coef.</th>
          </tr>
        </thead>
        <tbody>
          {adm.linhas
            .filter((l) => l.quantidade > 0)
            .map((l) => {
              const multiplicador = l.multiplicador ?? adm.multiplicador_padrao;
              return (
                <tr key={l.id}>
                  <td>{l.codigo}</td>
                  <td>{l.descricao || descricaoDe(l.codigo)}</td>
                  <td className="num">{formatarQuantidade(l.quantidade)}</td>
                  <td className="num">{formatarQuantidade(multiplicador)}</td>
                  <td className="num">{formatarQuantidade(l.quantidade * multiplicador)}</td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </section>
  );
}
